import React from "react";

import { Box, Card, CardContent, ClickAwayListener, List, ListItem, ListItemText, Typography } from "@material-ui/core";
import ExpandLessRoundedIcon from "@material-ui/icons/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@material-ui/icons/ExpandMoreRounded";
import { useTheme, withStyles } from "@material-ui/core/styles";

import Spacer from "components/Spacer";

import { DemographicIndicator } from "modules/customer/tools/location/demographics/catchmentDemographics";
import {
    selectDemographicIndicator,
    selectDemographicIndicatorDropdownOptions,
    setDemographicIndicator
} from "modules/customer/tools/location/demographics/demographicsSlice";
import { useAppDispatch, useAppSelector } from "store";

const StyledCard = withStyles(theme => ({
    root: {
        // @ts-ignore
        backgroundColor: theme.palette.quaternary.dark,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        width: theme.spacing(43),
        borderRadius: (props: any) => props["data-is-open"] ? "6px 6px 0px 0px" : "6px",
        cursor: "pointer"
    }
}))(Card);

const StyledCardContent = withStyles(theme => ({
    root: {
        "&:last-child": {
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2)
        }
    }
}))(CardContent);

const StyledListCard = withStyles(theme => ({
    root: {
        position: "absolute",
        top: theme.spacing(-2),
        // @ts-ignore
        backgroundColor: theme.palette.quaternary.dark,
        width: theme.spacing(43),
        maxHeight: theme.spacing(40),
        overflow: "auto",
        borderRadius: "0px 0px 6px 6px",
        borderTopStyle: "solid",
        zIndex: theme.zIndex.drawer
    }
}))(Card);

const DemographicIndicatorDropdown: React.FC = () => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const [isOpen, setIsOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const demographicIndicator = useAppSelector(selectDemographicIndicator);
    const demographicIndicators = useAppSelector(selectDemographicIndicatorDropdownOptions);

    const handleClick = () => {
        setIsOpen(prevIsOpen => !prevIsOpen);
    };

    const handleClickAway = () => {
        setIsOpen(false);
    };

    const handleDemographicIndicatorClick = (newDemographicIndicator: DemographicIndicator) => {
        dispatch(setDemographicIndicator(newDemographicIndicator));
        setIsOpen(false);
    };

    return (
        <>
            <StyledCard ref={anchorRef} elevation={0} onClick={handleClick} data-is-open={isOpen}>
                <StyledCardContent>
                    <Box display="flex" alignItems="center">
                        <Typography variant="subtitle2" component="div">
                            Demographics characteristics
                        </Typography>
                        <Spacer />
                        {isOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                    </Box>
                    <Box display="flex" alignItems="center" paddingTop={1} minHeight={theme.spacing(3.5)}>
                        <Typography variant="body2" component="div" color="textSecondary">
                            {demographicIndicators.get(demographicIndicator) ?? "Unknown"}
                        </Typography>
                    </Box>
                </StyledCardContent>
            </StyledCard>
            <Box position="relative">
                {isOpen && (
                    <ClickAwayListener onClickAway={handleClickAway}>
                        <StyledListCard>
                            <List>
                                {Array.from(demographicIndicators.entries()).map(indicator =>
                                    <ListItem
                                        key={indicator[0]}
                                        dense
                                        divider
                                        button
                                        selected={indicator[0] === demographicIndicator}
                                        onClick={() => handleDemographicIndicatorClick(indicator[0])}
                                    >
                                        <ListItemText
                                            primary={indicator[1]}
                                            primaryTypographyProps={{
                                                variant: "subtitle1",
                                                component: "div"
                                            }}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </StyledListCard>
                    </ClickAwayListener>
                )}
            </Box>
        </>
    );
};

export default DemographicIndicatorDropdown;
